import { AddOutlined } from '@mui/icons-material'
import { CardMedia, CardMediaProps, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Link } from 'react-router-dom'
import { useNonprofitContext } from '../../hooks/commonOrgContext'
import { useScheduledActivityContext } from '../../hooks/useScheduledActivityContext'
import useOrgStyles from "../../styles/orgStyles"
import { pathToLogoPlaceholder } from '../../util/icons'
import { resizedImageUrl } from '../../util/resizedImageUrl'
import { OrgStatus } from '@fieldday/fielddayportal-model'

export default function ActivityPhotoAndLogos({ activityImageProps }: { activityImageProps: CardMediaProps }) {
  const npoStyles = useOrgStyles()
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))

  const { npo } = useNonprofitContext()
  const { scheduledActivity: sa } = useScheduledActivityContext()
  const orgUnit = sa.orgUnit
  const placeholderNpo = npo.status === OrgStatus.Placeholder
  const hasActivityImg = sa.activity?.bannerImage

  const activityImage = () => {
    return (
      <Grid item xs={7} sm={4} paddingRight={mobileView ? 0 : 2} >
        <CardMedia {...activityImageProps} />
      </Grid>
    )
  }

  const npoLogoLink = () => {
    return (
      <Link to={`/nonprofits/${npo.handle}`}>
        <CardMedia title={`${npo.name} logo`}
          component="img"
          className={npoStyles.logoImage}
          image={resizedImageUrl(npo.logo, 300, 200) || pathToLogoPlaceholder}
        />
      </Link>
    )
  }

  const teamLogoLink = () => {
    return (
      <Link to={`/teams/${orgUnit?.handle}`}>
        <CardMedia title={`${orgUnit?.name} logo`}
          component="img"
          className={hasActivityImg && placeholderNpo && !mobileView ? undefined : npoStyles.logoImage}
          image={resizedImageUrl(orgUnit?.logo, 300, 200) || pathToLogoPlaceholder}
          sx={{ maxHeight: hasActivityImg && placeholderNpo ? '4rem' : 'auto' }}
        />
      </Link>
    )
  }

  if (!orgUnit) {
    return (
      <>
        {mobileView ?
          <Grid container alignItems="center">
            {sa.activity?.bannerImage &&
              <Grid item xs={8} paddingRight={2}>
                <CardMedia {...activityImageProps} />
              </Grid>
            }
            <Grid item xs={4}>
              {npoLogoLink()}
            </Grid>
          </Grid>
          :
          <>
            {activityImage()}
            <Grid item xs={3}>
              {npoLogoLink()}
            </Grid>
          </>
        }
      </>
    )
  }

  if (orgUnit && hasActivityImg && placeholderNpo) {
    return (
      <>
        {activityImage()}
        <Grid item xs={12} sm={3}>
          {mobileView ?
            <Grid container columns={12} alignItems="center" sx={{ mt: 2 }} justifyContent="center" >
              <Grid item xs={3} >
                {teamLogoLink()}
              </Grid>
              <AddOutlined sx={{ color: theme.palette.neutral.main, m: 1.5 }}/>
              <Grid item xs={7} >
                <Typography variant={"h5"} sx={{ marginTop: "auto", marginBottom: 0 }}>
                  {npo.name}
                </Typography>
              </Grid>
            </Grid>
            :
            <Stack sx={{ alignItems: "center", justifyContent: 'center' }}>
              {teamLogoLink()}
              <AddOutlined sx={{ color: theme.palette.neutral.main, mt: 1.5, mb: .5 }}/>
              <Typography variant="h6" sx={{ textAlign: "center", margin: 0 }}>
                {npo.name}
              </Typography>
            </Stack>
          }
        </Grid>
      </>
    )
  }

  if (orgUnit && !hasActivityImg && placeholderNpo) {
    return (
      <>
        <Grid item xs={12} sm={12}>
          <Grid container
            alignItems="center"
            sx={{ mt: mobileView ? 2 : 0 }}
            justifyContent="start"
          >
            <Grid item xs={3} >
              {teamLogoLink()}
            </Grid>
            <AddOutlined sx={{ color: theme.palette.neutral.main, mx: 1.5 }} />
            <Grid item xs={7} >
              <Typography variant={mobileView ? "h5" : "h6"}>
                {npo.name}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      {activityImage()}
      <Grid item xs={7} sm={3} >
        <Grid container
          columns={3}
          alignItems="center"
          sx={{ mt: mobileView ? 2 : 0 }}
          justifyContent={{ xs: "center", sm: "flex-end", md: "center", lg: "flex-end" }}
        >
          <Grid item xs={1} >
            {teamLogoLink()}
          </Grid>
          <AddOutlined sx={{ color: theme.palette.neutral.main, mx: 1.5 }} />
          <Grid item xs={1}>
            {npoLogoLink()}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
